import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLogin } from '@/hooks';
import { Button, Input } from '@/components/ui';
import { LOGO_FULL } from '@/lib/brand';

type LoginFormData = {
  email: string;
  password: string;
};

export function LoginPage(): JSX.Element {
  const { t } = useTranslation('auth');
  const loginMutation = useLogin();

  const createSchema = () =>
    z.object({
      email: z.string().email(t('validation.emailInvalid')),
      password: z.string().min(1, t('validation.passwordRequired')),
    });

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<LoginFormData>({
    resolver: zodResolver(createSchema()),
  });

  const onSubmit = async (data: LoginFormData) => {
    try {
      await loginMutation.mutateAsync(data);
    } catch {
      // Error is handled by mutation
    }
  };

  const errorMessage = loginMutation.error ? loginMutation.error.message : null;
  const isBusy = isSubmitting || loginMutation.isPending;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-12 sm:px-6 lg:px-8">
      <div className="w-full max-w-md space-y-8">
        <div className="flex flex-col items-center gap-4">
          <img
            src={LOGO_FULL}
            alt="StoneHartDen"
            className="h-14 w-auto object-contain sm:h-16"
          />
          <h2 className="text-center font-heading text-heading-xl text-brand-950">
            {t('login.title')}
          </h2>
        </div>
        <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
          <Input
            {...register('email')}
            id="email"
            type="email"
            autoComplete="email"
            label={t('login.email')}
            placeholder={t('login.email')}
            error={errors.email?.message}
          />
          <Input
            {...register('password')}
            id="password"
            type="password"
            autoComplete="current-password"
            label={t('login.password')}
            placeholder={t('login.password')}
            error={errors.password?.message}
          />

          {errorMessage ? (
            <div className="rounded-md bg-red-50 p-4">
              <p className="text-sm text-red-800">{errorMessage}</p>
            </div>
          ) : null}

          <Button type="submit" fullWidth loading={isBusy} disabled={isBusy}>
            {isBusy ? t('app.loading', { ns: 'common' }) : t('login.submit')}
          </Button>

          <div className="text-center">
            <Link
              to="/register"
              className="font-medium text-brand-600 transition-colors hover:text-brand-400"
            >
              {t('login.registerLink')}
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
